import { useState, useEffect, type ReactNode } from 'react';
import { X, Maximize2, ChevronLeft } from 'lucide-react';
import { Drawer } from './Drawer';

export function ListPreviewDrawer({
  open,
  onClose,
  title,
  subtitle,
  typeLabel,
  openFullHref: _openFullHref,
  openFullLabel,
  children,
  expandedChildren,
}: {
  open: boolean;
  onClose: () => void;
  title: string;
  subtitle?: string;
  /** Small label shown above the title (e.g. "Application", "Candidate"). */
  typeLabel?: string;
  /** Kept for API compatibility; no longer navigates on Expand click. */
  openFullHref?: string;
  /** Label for the footer "Open full" CTA (defaults to "Open full"). */
  openFullLabel?: string;
  children: ReactNode;
  /**
   * Optional richer content shown when the panel is in expanded (full-width) mode.
   * Falls back to `children` if not provided.
   */
  expandedChildren?: ReactNode;
}) {
  const [expanded, setExpanded] = useState(false);

  // Reset expanded state whenever the drawer is closed
  useEffect(() => {
    if (!open) setExpanded(false);
  }, [open]);

  const handleClose = () => {
    setExpanded(false);
    onClose();
  };

  return (
    <Drawer
      open={open}
      onClose={handleClose}
      showCloseButton={false}
      title={undefined}
      // In expanded mode: take over the full content capsule, no backdrop dimming
      // In compact mode:  480 px side panel with semi-transparent backdrop
      panelMaxWidthPx={expanded ? 999_999 : 480}
      showBackdrop={!expanded}
      closeOnBackdrop={!expanded}
      widthClass="w-full max-w-[100vw] lg:max-w-[480px]"
      panelClassName="transition-[width] duration-200 ease-in-out"
      ariaLabel={title}
    >
      <div className="flex min-h-0 flex-1 flex-col">
        {/* ── Header: type label + title + expand/collapse + close ── */}
        <header className="flex shrink-0 flex-col gap-0.5 border-b vscode-border px-4 pb-3 pt-3">
          <div className="flex items-center justify-between gap-2">
            {/* Left: collapse-back arrow (expanded) OR type label (compact) */}
            {expanded ? (
              <button
                type="button"
                onClick={() => setExpanded(false)}
                className="flex items-center gap-1 rounded-lg px-1.5 py-1 text-[11px] font-medium vscode-text-tertiary transition-colors hover:vscode-bg-hover hover:vscode-text-primary"
                aria-label="Collapse panel"
                title="Collapse panel"
              >
                <ChevronLeft className="h-3.5 w-3.5" />
                {typeLabel ?? 'Back'}
              </button>
            ) : typeLabel ? (
              <span className="text-[11px] font-medium uppercase tracking-wider vscode-text-tertiary">
                {typeLabel}
              </span>
            ) : <span />}

            {/* Right: Expand ↔ Collapse toggle + Close */}
            <div className="flex items-center gap-0.5">
              {!expanded && (
                <button
                  type="button"
                  onClick={() => setExpanded(true)}
                  className="rounded-lg p-1.5 vscode-text-tertiary transition-colors hover:vscode-bg-hover hover:vscode-text-primary"
                  aria-label="Expand panel"
                  title="Expand panel"
                >
                  <Maximize2 className="h-4 w-4" />
                </button>
              )}
              <button
                type="button"
                onClick={handleClose}
                className="rounded-lg p-1.5 vscode-text-tertiary transition-colors hover:vscode-bg-hover hover:vscode-text-primary"
                aria-label="Close"
              >
                <X className="h-4 w-4" />
              </button>
            </div>
          </div>
          <h2 className="truncate text-[15px] font-semibold vscode-text-primary">{title}</h2>
          {subtitle ? <p className="truncate text-[13px] vscode-text-tertiary">{subtitle}</p> : null}
        </header>

        {/* ── Body: fills remaining panel height, scrolls internally ── */}
        <div className="min-h-0 flex-1 overflow-y-auto overscroll-y-contain">
          {expanded ? (expandedChildren ?? children) : children}
        </div>

        {/* ── Footer: Open full (emerald) + Close ── */}
        <footer className="flex shrink-0 items-center justify-between gap-2 border-t vscode-border px-4 py-3">
          <button
            type="button"
            onClick={() => setExpanded(true)}
            className="rounded-lg bg-emerald-600 px-3 py-1.5 text-[13px] font-medium text-white transition-colors hover:bg-emerald-700"
          >
            {openFullLabel ?? 'Open full'}
          </button>
          <button
            type="button"
            onClick={handleClose}
            className="rounded-lg border border-[var(--vscode-border-primary)] px-3 py-1.5 text-[13px] font-medium vscode-text-secondary transition-colors hover:vscode-bg-hover hover:vscode-text-primary"
          >
            Close
          </button>
        </footer>
      </div>
    </Drawer>
  );
}
